// §116: renders an invoice as a proper letterhead document and opens it for
// print/save-as-PDF - same "browser print-to-PDF via a popup window"
// approach as lib/reportExport.ts's printReportAsPdf, deliberately avoiding
// a server-side PDF-generation dependency for what's fundamentally a
// formatted page. Used by both InvoiceDetailPage (the real thing) and
// InvoiceTemplateDesignerPage (a live preview using dummy data), so the
// exact same renderer is what an MSP Admin previews and what a customer
// actually receives.
import { PLATFORM_LOGO_URL, type InvoiceTemplate } from '../api/platformBranding';
import type { CreditNote, Invoice } from '../types/api';

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function nl2br(value: string): string {
  return escapeHtml(value).replace(/\n/g, '<br />');
}

function money(value: string | number) {
  return `£${Number(value).toFixed(2)}`;
}

function buildInvoiceHtml(invoice: Invoice, template: InvoiceTemplate): string {
  const accent = template.accentColor ?? '#1d4ed8';
  const addressLines = [template.addressLine1, template.addressLine2, [template.city, template.postcode].filter(Boolean).join(' '), template.country]
    .filter((l): l is string => !!l && l.trim().length > 0);

  const logoUrl = template.hasLogo ? `${window.location.origin}${PLATFORM_LOGO_URL}` : null;
  const companyName = template.companyName ?? 'Oakford Technology';

  const lines = invoice.lines ?? [];
  const rowsHtml = lines
    .map(
      (l) => `<tr>
        <td>${escapeHtml(l.description)}</td>
        <td>${escapeHtml(l.site?.name ?? '-')}</td>
        <td class="num">${money(l.netAmount)}</td>
        <td class="num">${money(l.vatAmount)}</td>
        <td class="num">${money(Number(l.netAmount) + Number(l.vatAmount))}</td>
      </tr>`,
    )
    .join('');

  return `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>Invoice ${escapeHtml(invoice.invoiceNumber)}</title>
        <style>
          body { font-family: -apple-system, Segoe UI, Arial, sans-serif; padding: 40px; color: #0f172a; }
          .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 3px solid ${accent}; padding-bottom: 20px; margin-bottom: 24px; }
          .header img { max-height: 56px; max-width: 220px; object-fit: contain; }
          .company-block { text-align: right; font-size: 11px; color: #475569; line-height: 1.5; }
          .company-block .name { font-size: 14px; font-weight: 700; color: #0f172a; margin-bottom: 2px; }
          h1 { font-size: 22px; letter-spacing: 0.04em; color: ${accent}; margin: 0 0 4px; }
          .meta { display: flex; justify-content: space-between; margin-bottom: 28px; font-size: 12px; }
          .meta .bill-to .name { font-weight: 700; font-size: 13px; color: #0f172a; }
          .meta .details { text-align: right; color: #475569; }
          table { width: 100%; border-collapse: collapse; font-size: 12px; margin-bottom: 20px; }
          th, td { text-align: left; padding: 8px 10px; border-bottom: 1px solid #e2e8f0; }
          th { text-transform: uppercase; font-size: 10px; color: #64748b; letter-spacing: 0.04em; }
          td.num, th.num { text-align: right; }
          .totals { display: flex; justify-content: flex-end; margin-bottom: 32px; }
          .totals table { width: 260px; margin: 0; }
          .totals td { border-bottom: none; padding: 4px 10px; }
          .totals .grand td { border-top: 1px solid #0f172a; font-weight: 700; padding-top: 8px; }
          .footer { border-top: 1px solid #e2e8f0; padding-top: 16px; font-size: 10px; color: #64748b; display: flex; justify-content: space-between; gap: 24px; }
          .footer .note { flex: 1; }
          @media print { body { padding: 16px; } }
        </style>
      </head>
      <body onload="window.print()">
        <div class="header">
          <div>
            ${logoUrl ? `<img src="${logoUrl}" alt="${escapeHtml(companyName)}" />` : `<div class="company-block"><div class="name">${escapeHtml(companyName)}</div></div>`}
          </div>
          <div class="company-block">
            <div class="name">${escapeHtml(companyName)}</div>
            ${addressLines.map((l) => `<div>${escapeHtml(l)}</div>`).join('')}
            ${template.vatNumber ? `<div>VAT: ${escapeHtml(template.vatNumber)}</div>` : ''}
            ${template.registrationNumber ? `<div>Company no: ${escapeHtml(template.registrationNumber)}</div>` : ''}
          </div>
        </div>

        <h1>INVOICE</h1>
        <div class="meta">
          <div class="bill-to">
            <div class="name">${escapeHtml(invoice.customerOrganisation?.name ?? '')}</div>
          </div>
          <div class="details">
            <div>Invoice ${escapeHtml(invoice.invoiceNumber)}</div>
            <div>Date: ${new Date(invoice.generatedAt).toLocaleDateString('en-GB')}</div>
            <div>Period: ${new Date(invoice.billingPeriodStart).toLocaleDateString('en-GB')} – ${new Date(invoice.billingPeriodEnd).toLocaleDateString('en-GB')}</div>
          </div>
        </div>

        <table>
          <thead>
            <tr><th>Description</th><th>Site</th><th class="num">Net</th><th class="num">VAT</th><th class="num">Total</th></tr>
          </thead>
          <tbody>${rowsHtml}</tbody>
        </table>

        <div class="totals">
          <table>
            <tr><td>Subtotal</td><td class="num">${money(invoice.subtotal)}</td></tr>
            <tr><td>VAT</td><td class="num">${money(invoice.vatTotal)}</td></tr>
            <tr class="grand"><td>Total due</td><td class="num">${money(invoice.grandTotal)}</td></tr>
          </table>
        </div>

        <div class="footer">
          ${template.footerNote ? `<div class="note">${nl2br(template.footerNote)}</div>` : '<div class="note"></div>'}
          ${template.bankDetails ? `<div class="note">${nl2br(template.bankDetails)}</div>` : ''}
        </div>
      </body>
    </html>
  `;
}

// Split into "open" (must happen synchronously inside the click handler,
// before any await) and "render" (can happen after an async fetch) -
// several browsers (Safari in particular) only allow window.open without
// triggering the pop-up blocker when it's called directly inside a
// synchronous user-gesture handler. InvoiceDetailPage needs to fetch the
// template first, so it opens the window immediately and renders into it
// once the fetch resolves; the designer's preview has everything already
// in memory and can just call printInvoice directly.
export function openInvoiceWindow(): Window {
  const win = window.open('', '_blank', 'width=900,height=1000');
  if (!win) {
    throw new Error("Could not open the print window - check your browser's pop-up blocker.");
  }
  return win;
}

export function renderInvoiceIntoWindow(win: Window, invoice: Invoice, template: InvoiceTemplate) {
  win.document.write(buildInvoiceHtml(invoice, template));
  win.document.close();
}

export function printInvoice(invoice: Invoice, template: InvoiceTemplate) {
  renderInvoiceIntoWindow(openInvoiceWindow(), invoice, template);
}

// Dummy data used by the template designer's live preview - a plausible-
// looking invoice so an MSP Admin can see roughly what a real one will look
// like without needing an actual invoice on hand.
export function buildPreviewInvoice(): Invoice {
  const now = new Date();
  return {
    id: 'preview-0000000000',
    customerOrganisationId: 'preview',
    invoiceNumber: 'ATI26010101',
    billingPeriodStart: new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString(),
    billingPeriodEnd: now.toISOString(),
    subtotal: '345.00',
    vatTotal: '69.00',
    grandTotal: '414.00',
    status: 'ISSUED',
    generatedAt: now.toISOString(),
    customerOrganisation: {
      id: 'preview',
      name: 'Example Customer Ltd',
      status: 'ACTIVE',
      createdAt: now.toISOString(),
      updatedAt: now.toISOString(),
      hasLogo: false,
      brandColor: null,
      invoiceEmail: null,
    },
    lines: [
      {
        id: 'l1',
        invoiceId: 'preview-0000000000',
        siteId: 's1',
        description: 'Dell Latitude 5440 Laptop x2',
        netAmount: '200.00',
        vatAmount: '40.00',
        sourceOrderLineId: 'ol1',
        sourceInvoiceScheduleId: null,
        site: { id: 's1', customerOrganisationId: 'preview', name: 'Head Office', address: null, status: 'ACTIVE', selfRegistrationEnabled: false, inviteCode: null, approvalThreshold: null, createdAt: now.toISOString(), updatedAt: now.toISOString() },
      },
      {
        id: 'l2',
        invoiceId: 'preview-0000000000',
        siteId: 's1',
        description: 'Managed Network Switches - contract instalment',
        netAmount: '145.00',
        vatAmount: '29.00',
        sourceOrderLineId: null,
        sourceInvoiceScheduleId: null,
        site: { id: 's1', customerOrganisationId: 'preview', name: 'Head Office', address: null, status: 'ACTIVE', selfRegistrationEnabled: false, inviteCode: null, approvalThreshold: null, createdAt: now.toISOString(), updatedAt: now.toISOString() },
      },
    ],
  };
}

// §20: credit note letterhead - same "print via popup window" approach and
// the same letterhead styling as buildInvoiceHtml, just headed "CREDIT
// NOTE" with the ATC document number and reason instead of a billing
// period, and no VAT-registration boilerplate duplicated (kept minimal by
// reusing the same template settings an invoice uses).
function buildCreditNoteHtml(creditNote: CreditNote, template: InvoiceTemplate): string {
  const accent = template.accentColor ?? '#1d4ed8';
  const addressLines = [template.addressLine1, template.addressLine2, [template.city, template.postcode].filter(Boolean).join(' '), template.country]
    .filter((l): l is string => !!l && l.trim().length > 0);

  const logoUrl = template.hasLogo ? `${window.location.origin}${PLATFORM_LOGO_URL}` : null;
  const companyName = template.companyName ?? 'Oakford Technology';

  const lines = creditNote.lines ?? [];
  const rowsHtml = lines
    .map(
      (l) => `<tr>
        <td>${escapeHtml(l.description)}</td>
        <td>${escapeHtml(l.site?.name ?? '-')}</td>
        <td class="num">${money(l.netAmount)}</td>
        <td class="num">${money(l.vatAmount)}</td>
        <td class="num">${money(Number(l.netAmount) + Number(l.vatAmount))}</td>
      </tr>`,
    )
    .join('');

  return `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>Credit Note ${escapeHtml(creditNote.creditNumber)}</title>
        <style>
          body { font-family: -apple-system, Segoe UI, Arial, sans-serif; padding: 40px; color: #0f172a; }
          .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 3px solid ${accent}; padding-bottom: 20px; margin-bottom: 24px; }
          .header img { max-height: 56px; max-width: 220px; object-fit: contain; }
          .company-block { text-align: right; font-size: 11px; color: #475569; line-height: 1.5; }
          .company-block .name { font-size: 14px; font-weight: 700; color: #0f172a; margin-bottom: 2px; }
          h1 { font-size: 22px; letter-spacing: 0.04em; color: ${accent}; margin: 0 0 4px; }
          .meta { display: flex; justify-content: space-between; margin-bottom: 12px; font-size: 12px; }
          .meta .bill-to .name { font-weight: 700; font-size: 13px; color: #0f172a; }
          .meta .details { text-align: right; color: #475569; }
          .reason { margin-bottom: 24px; font-size: 12px; color: #475569; }
          table { width: 100%; border-collapse: collapse; font-size: 12px; margin-bottom: 20px; }
          th, td { text-align: left; padding: 8px 10px; border-bottom: 1px solid #e2e8f0; }
          th { text-transform: uppercase; font-size: 10px; color: #64748b; letter-spacing: 0.04em; }
          td.num, th.num { text-align: right; }
          .totals { display: flex; justify-content: flex-end; margin-bottom: 32px; }
          .totals table { width: 260px; margin: 0; }
          .totals td { border-bottom: none; padding: 4px 10px; }
          .totals .grand td { border-top: 1px solid #0f172a; font-weight: 700; padding-top: 8px; }
          .footer { border-top: 1px solid #e2e8f0; padding-top: 16px; font-size: 10px; color: #64748b; display: flex; justify-content: space-between; gap: 24px; }
          .footer .note { flex: 1; }
          @media print { body { padding: 16px; } }
        </style>
      </head>
      <body onload="window.print()">
        <div class="header">
          <div>
            ${logoUrl ? `<img src="${logoUrl}" alt="${escapeHtml(companyName)}" />` : `<div class="company-block"><div class="name">${escapeHtml(companyName)}</div></div>`}
          </div>
          <div class="company-block">
            <div class="name">${escapeHtml(companyName)}</div>
            ${addressLines.map((l) => `<div>${escapeHtml(l)}</div>`).join('')}
            ${template.vatNumber ? `<div>VAT: ${escapeHtml(template.vatNumber)}</div>` : ''}
            ${template.registrationNumber ? `<div>Company no: ${escapeHtml(template.registrationNumber)}</div>` : ''}
          </div>
        </div>

        <h1>CREDIT NOTE</h1>
        <div class="meta">
          <div class="bill-to">
            <div class="name">${escapeHtml(creditNote.customerOrganisation?.name ?? '')}</div>
          </div>
          <div class="details">
            <div>Credit note ${escapeHtml(creditNote.creditNumber)}</div>
            <div>Date: ${new Date(creditNote.generatedAt).toLocaleDateString('en-GB')}</div>
            ${creditNote.invoice ? `<div>Against invoice ${escapeHtml(creditNote.invoice.invoiceNumber)}</div>` : ''}
          </div>
        </div>
        <div class="reason">${escapeHtml(creditNote.reason)}</div>

        <table>
          <thead>
            <tr><th>Description</th><th>Site</th><th class="num">Net</th><th class="num">VAT</th><th class="num">Total</th></tr>
          </thead>
          <tbody>${rowsHtml}</tbody>
        </table>

        <div class="totals">
          <table>
            <tr><td>Subtotal</td><td class="num">${money(creditNote.subtotal)}</td></tr>
            <tr><td>VAT</td><td class="num">${money(creditNote.vatTotal)}</td></tr>
            <tr class="grand"><td>Total credited</td><td class="num">${money(creditNote.grandTotal)}</td></tr>
          </table>
        </div>

        <div class="footer">
          ${template.footerNote ? `<div class="note">${nl2br(template.footerNote)}</div>` : '<div class="note"></div>'}
          ${template.bankDetails ? `<div class="note">${nl2br(template.bankDetails)}</div>` : ''}
        </div>
      </body>
    </html>
  `;
}

export function renderCreditNoteIntoWindow(win: Window, creditNote: CreditNote, template: InvoiceTemplate) {
  win.document.write(buildCreditNoteHtml(creditNote, template));
  win.document.close();
}
